// EXTERNAL DEPENDENCIES
import React from 'react'

// HELPERS
import { usePrefixedContent } from '../../../../utils/content-helpers'
import { useFormattedAddresses } from '../../../../hooks'

// COMPONENTS
import { BodyText, RadioButton, Row } from '@paypalcorp/pp-react'
import { WithLoadingShimmer } from '../../loading/with-loading-shimmer'

// CONSTANTS
import {
  CITY_STATE_COUNTRY_ADDRESS_PREFERENCE,
  DO_NOT_SHOW_ADDRESS_PREFERENCE,
  FULL_ADDRESS_ADDRESS_PREFERENCE,
  STATE_COUNTRY_ADDRESS_PREFERENCE,
} from '@/constants/addresses'

// TYPINGS
import { PortableAddress } from '../../../../types/world-ready'

// TEST IDS
import { LOCATION_NAME_SECTION_TEST_ID } from '../../../../constants/test-ids'

/**
 * Displays a radio group to allow the user to select how they
 * want their address to appear
 */
export default function LocationNameSection({
  businessAddress,
  selectedLocationDisplayPreference,
  updateSelectedLocationDisplayPreference,
}: {
  businessAddress: PortableAddress
  selectedLocationDisplayPreference: string
  updateSelectedLocationDisplayPreference: (
    newLocationPreference: string,
  ) => void
}) {
  const getContent = usePrefixedContent('business-info-sections.index.')
  const formattedAddresses = useFormattedAddresses(businessAddress)
  const doNotShowAddressOption = getContent('doNotDisplayLocationOption')

  const uniqueIdentifier = Math.round(10000000 * Math.random()) //NOSONAR

  return (
    <div data-testid={LOCATION_NAME_SECTION_TEST_ID}>
      <Row noGutters>
        <BodyText strong>{getContent('businessLocationSectionLabel')}</BodyText>
      </Row>
      <WithLoadingShimmer isLoading={!businessAddress?.addressLine1}>
        <RadioButton
          name={`LocationNameSectionRadio-${uniqueIdentifier}`}
          options={[
            {
              label: formattedAddresses[FULL_ADDRESS_ADDRESS_PREFERENCE],
              value: FULL_ADDRESS_ADDRESS_PREFERENCE,
            },
            {
              label: formattedAddresses[CITY_STATE_COUNTRY_ADDRESS_PREFERENCE],
              value: CITY_STATE_COUNTRY_ADDRESS_PREFERENCE,
            },
            {
              label: formattedAddresses[STATE_COUNTRY_ADDRESS_PREFERENCE],
              value: STATE_COUNTRY_ADDRESS_PREFERENCE,
            },
            {
              label: doNotShowAddressOption,
              value: DO_NOT_SHOW_ADDRESS_PREFERENCE,
            },
          ]}
          onChange={(e) =>
            updateSelectedLocationDisplayPreference(e.target.value)
          }
          value={selectedLocationDisplayPreference}
        />
      </WithLoadingShimmer>
    </div>
  )
}
